import { Spinner } from "@dash/spinner";
import { useTheme } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { MdClose } from "react-icons/md";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

type Props = {
  file: Uint8Array | Blob | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
};

type DocumentSource = { data: Uint8Array };

function toDocumentSource(file: Uint8Array | Blob): Promise<DocumentSource> {
  if (file instanceof Blob) {
    return file.arrayBuffer().then((ab) => ({ data: new Uint8Array(ab) }));
  }
  const copy = new Uint8Array(file.byteLength);
  copy.set(file);
  return Promise.resolve({ data: copy });
}

export const FullScreenPdfView: React.FC<Props> = ({ file, loading, error, onClose }) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [documentSource, setDocumentSource] = useState<DocumentSource | null>(null);

  const updateScale = (page: { getViewport: (opts: { scale: number }) => { width: number; height: number } }) => {
    if (containerRef.current) {
      const { clientWidth, clientHeight } = containerRef.current;
      const viewport = page.getViewport({ scale: 1 });

      let newScale = clientWidth / viewport.width;
      const newHeight = viewport.height * newScale;

      if (newHeight > clientHeight) {
        newScale = clientHeight / viewport.height;
      }

      setScale(newScale);
    }
  };

  const onDocumentLoadSuccess = (pdf: { getPage: (n: number) => Promise<{ getViewport: (opts: { scale: number }) => { width: number; height: number } }> }) => {
    pdf.getPage(1).then(updateScale);
  };

  useEffect(() => {
    if (!file) {
      setDocumentSource(null);
      return;
    }
    let cancelled = false;
    toDocumentSource(file).then((source) => {
      if (!cancelled) setDocumentSource(source);
    });
    return () => {
      cancelled = true;
      setDocumentSource(null);
    };
  }, [file]);

  useEffect(() => {
    if (!documentSource || !containerRef.current) return;
    const loadingTask = pdfjs.getDocument(documentSource);
    loadingTask.promise.then((pdf) => {
      pdf.getPage(1).then(updateScale);
    });
  }, [documentSource]);

  useEffect(() => {
    if (!documentSource) return;
    const handleResize = () => {
      const loadingTask = pdfjs.getDocument(documentSource);
      loadingTask.promise.then((pdf) => {
        pdf.getPage(1).then(updateScale);
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [documentSource]);

  const closeButton = (
    <button
      onClick={onClose}
      style={{
        position: "absolute",
        top: "8px",
        right: "8px",
        background: "white",
        border: "none",
        borderRadius: "50%",
        padding: "8px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.25)",
      }}
    >
      <MdClose size={24} color="#000" />
    </button>
  );

  if (loading) {
    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "white",
          borderRadius: theme.smallBorderRadius,
          position: "relative",
        }}
      >
        <Spinner color={theme.colors.primary} size="32px" />
        {closeButton}
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "16px",
          color: "red",
          background: "white",
          borderRadius: theme.smallBorderRadius,
          position: "relative",
        }}
      >
        <p>{error}</p>
        {closeButton}
      </div>
    );
  }

  if (!file || !documentSource) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        minHeight: "80vh",
        position: "relative",
        background: "white",
        borderRadius: theme.smallBorderRadius,
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Document
        file={documentSource}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Spinner color={theme.colors.primary} size="32px" />
          </div>
        }
        error="Nie udało się załadować podglądu."
      >
        <Page
          pageNumber={1}
          scale={scale}
          css={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      </Document>
      {closeButton}
    </div>
  );
};
